import React, { useEffect, useMemo, useRef, useState } from "react";
import PitchCurveAudioPanel from "./PitchCurveAudioPanel";
import CurveEditingBlock from "./CurveEditingBlock";

const COLORS = ["green", "yellow", "red"];

const COLOR_META = {
  green: {
    label: "Good",
    short: "G",
    dotClass: "bg-green-500",
    bgClass: "bg-green-50",
    borderClass: "border-green-300",
    textClass: "text-green-800",
    softClass: "bg-green-100",
    ringClass: "shadow-green-500/20",
  },
  yellow: {
    label: "Almost",
    short: "Y",
    dotClass: "bg-yellow-400",
    bgClass: "bg-yellow-50",
    borderClass: "border-yellow-300",
    textClass: "text-yellow-800",
    softClass: "bg-yellow-100",
    ringClass: "shadow-yellow-500/20",
  },
  red: {
    label: "Needs work",
    short: "R",
    dotClass: "bg-red-500",
    bgClass: "bg-red-50",
    borderClass: "border-red-300",
    textClass: "text-red-800",
    softClass: "bg-red-100",
    ringClass: "shadow-red-500/20",
  },
};

function cx(...classes) {
  return classes.filter(Boolean).join(" ");
}

function colorOf(value) {
  return COLOR_META[value] || COLOR_META.green;
}

function SmoothReveal({ show, children, className = "" }) {
  return (
    <div
      className={cx(
        "grid transition-all duration-500 ease-out",
        show
          ? "grid-rows-[1fr] opacity-100 translate-y-0"
          : "grid-rows-[0fr] opacity-0 -translate-y-2 pointer-events-none",
        className
      )}
    >
      <div className="min-h-0 overflow-hidden">{children}</div>
    </div>
  );
}

const isRateableChar = (char) => /[\p{L}\p{N}]/u.test(char);

const getAudioFetchUrl = (audioUrl) => {
  if (!audioUrl) return null;
  if (audioUrl.startsWith("http")) return encodeURI(audioUrl);
  return encodeURI(`${process.env.REACT_APP_USER_API_URL}${audioUrl}`);
};

const getNarratorAudioFetchUrl = (narrator) => {
  if (!narrator) return null;

  if (narrator.audio_url) {
    return getAudioFetchUrl(narrator.audio_url);
  }

  if (narrator.file_narrator) {
    if (narrator.file_narrator.startsWith("http")) {
      return encodeURI(narrator.file_narrator);
    }

    const path = narrator.file_narrator.startsWith("/")
      ? narrator.file_narrator
      : `/${narrator.file_narrator}`;

    return encodeURI(`${process.env.REACT_APP_USER_API_URL}${path}`);
  }

  return null;
};

const splitWords = (text = "") =>
  String(text)
    .trim()
    .split(/\s+/)
    .filter(Boolean);

const buildRatings = (text = "") =>
  splitWords(text).map((word, wordIndex) => ({
    word,
    index: wordIndex,
    rating: "green",
    letters: Array.from(word).map((char, letterIndex) => ({
      char,
      index: letterIndex,
      rating: isRateableChar(char) ? "green" : null,
      rateable: isRateableChar(char),
    })),
  }));

function getUserNBest(assessment) {
  return assessment?.pronunciation?.NBest?.[0] || null;
}

function getChapterDisplay(comment, payloadBase) {
  return (
    payloadBase?.chapterTitle ||
    comment?.chapterTitle ||
    comment?.file ||
    payloadBase?.file ||
    ""
  );
}

function tintClasses(rating) {
  const meta = colorOf(rating);
  return cx("border", meta.borderClass, meta.bgClass);
}

function getMarkedCount(sentenceRating, ratings) {
  let count = sentenceRating !== "green" ? 1 : 0;

  for (const word of ratings || []) {
    if (word.rating && word.rating !== "green") count += 1;

    for (const letter of word.letters || []) {
      if (letter.rating && letter.rating !== "green") count += 1;
    }
  }

  return count;
}

function getNestedPitchPayloadForBezier(assessment) {
  return (
    assessment?.intonation?.intensity ||
    assessment?.intonation ||
    assessment?.intensity ||
    null
  );
}

function normalizePitchForBezier(assessment) {
  const intensity = getNestedPitchPayloadForBezier(assessment);

  const xs = intensity?.pitch?.xs || [];
  const ys = intensity?.pitch?.ys || [];

  if (!Array.isArray(xs) || !Array.isArray(ys)) return [];
  if (xs.length < 2 || ys.length < 2) return [];

  const pairs = [];

  for (let i = 0; i < Math.min(xs.length, ys.length); i += 1) {
    const x = Number(xs[i]);
    const y = Number(ys[i]);

    if (!Number.isFinite(x)) continue;
    if (!Number.isFinite(y) || y <= 0 || y < 55) continue;

    pairs.push({
      time: x,
      hz: y,
    });
  }

  if (pairs.length < 2) return [];

  const startTime = pairs[0].time;
  const endTime = pairs[pairs.length - 1].time;
  const span = endTime - startTime || 1;

  return pairs.map((point) => ({
    t: Math.max(0, Math.min(1, (point.time - startTime) / span)),
    time: point.time,
    hz: point.hz,
  }));
}

function getPitchTimeBounds(assessment) {
  const intensity = getNestedPitchPayloadForBezier(assessment);

  const xs = intensity?.pitch?.xs || [];
  const ys = intensity?.pitch?.ys || [];

  if (!Array.isArray(xs) || !Array.isArray(ys)) {
    return {
      start: 0,
      end: 1,
    };
  }

  const voicedTimes = [];

  for (let i = 0; i < Math.min(xs.length, ys.length); i += 1) {
    const x = Number(xs[i]);
    const y = Number(ys[i]);

    if (Number.isFinite(x) && Number.isFinite(y) && y > 0 && y >= 55) {
      voicedTimes.push(x);
    }
  }

  if (voicedTimes.length < 2) {
    return {
      start: Number(xs[0] ?? 0),
      end: Number(xs[xs.length - 1] ?? 1),
    };
  }

  return {
    start: voicedTimes[0],
    end: voicedTimes[voicedTimes.length - 1],
  };
}

function CircleRadioGroup({ value, onChange, compact = false }) {
  return (
    <div
      className={cx(
        "inline-flex items-center rounded-full border border-slate-200 bg-white shadow-sm",
        compact ? "gap-0.5 px-1.5 py-1" : "gap-1 px-2 py-1.5"
      )}
    >
      {COLORS.map((color) => {
        const active = value === color;
        const meta = COLOR_META[color];

        const activeClass =
          color === "green"
            ? "bg-green-500 text-white border-green-500 shadow-green-500/20"
            : color === "yellow"
              ? "bg-yellow-400 text-yellow-950 border-yellow-400 shadow-yellow-400/20"
              : "bg-red-500 text-white border-red-500 shadow-red-500/20";

        const idleClass =
          color === "green"
            ? "text-green-600 border-transparent hover:bg-green-50"
            : color === "yellow"
              ? "text-yellow-600 border-transparent hover:bg-yellow-50"
              : "text-red-600 border-transparent hover:bg-red-50";

        return (
          <button
            key={color}
            type="button"
            onClick={() => onChange(color)}
            title={meta.label}
            aria-label={meta.label}
            className={cx(
              "flex items-center justify-center rounded-full border font-black leading-none transition",
              "focus:outline-none focus:ring-4 focus:ring-slate-200",
              compact ? "h-6 w-6 text-[10px]" : "h-8 w-8 text-xs",
              active ? activeClass : idleClass,
              active && "shadow-md"
            )}
          >
            {meta.short}
          </button>
        );
      })}
    </div>
  );
}

function LetterButton({ letter, onClick }) {
  if (!letter.rateable) {
    return (
      <span
        className="inline-flex h-7 min-w-3 items-center justify-center text-sm font-black text-slate-400"
        title="Punctuation is not rated"
      >
        {letter.char}
      </span>
    );
  }

  const rating = letter.rating || "green";

  const classes =
    rating === "green"
      ? "border-green-200 bg-white text-slate-800 hover:bg-green-50"
      : rating === "yellow"
        ? "border-yellow-300 bg-yellow-100 text-yellow-800 shadow-sm"
        : "border-red-300 bg-red-100 text-red-700 shadow-sm";

  return (
    <button
      type="button"
      onClick={onClick}
      title="Click to cycle: green → yellow → red"
      aria-label={`Rate letter ${letter.char}`}
      className={cx(
        "inline-flex h-7 min-w-7 items-center justify-center rounded-lg border px-1.5 text-sm font-black leading-none transition hover:-translate-y-0.5",
        "focus:outline-none focus:ring-4 focus:ring-slate-200",
        classes
      )}
    >
      {letter.char}
    </button>
  );
}

function RateMeCollapsedPreview({
  sentence,
  hasAudio,
  canPlay,
  loadingAudio,
  isPlayingAudio,
  playRecording,
  audioError,
  onExpand,
  alreadyAnswered = false,
}) {
  const displaySentence = String(sentence || "").trim();

  return (
    <div
      className={cx(
        "mt-4 min-w-0 max-w-full overflow-hidden rounded-[26px] border px-4 py-4 shadow-sm transition hover:shadow-md sm:px-5",
        alreadyAnswered
          ? "border-green-300 bg-gradient-to-br from-white via-green-50 to-emerald-50"
          : "border-blue-300 bg-gradient-to-br from-white via-blue-50 to-sky-50"
      )}
    >
      <div className="flex min-w-0 flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <button
          type="button"
          onClick={alreadyAnswered ? undefined : onExpand}
          disabled={alreadyAnswered}
          className="flex min-w-0 flex-1 items-start gap-3 text-left disabled:cursor-default"
        >
          <div
            className={cx(
              "mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl border bg-white text-sm font-black shadow-sm",
              alreadyAnswered
                ? "border-green-300 text-green-700"
                : "border-blue-200 text-blue-700"
            )}
          >
            {alreadyAnswered ? "✓" : "▶"}
          </div>

          <div className="min-w-0 flex-1">
            <div
              className={cx(
                "mb-1 text-[10px] font-black uppercase tracking-[0.18em]",
                alreadyAnswered ? "text-green-700" : "text-blue-600"
              )}
            >
              Pronunciation request
            </div>

            {displaySentence ? (
              <div className="line-clamp-2 select-text text-[16px] font-black leading-6 text-slate-950 sm:text-[17px]">
                “{displaySentence}”
              </div>
            ) : (
              <div className="text-[16px] font-black leading-6 text-slate-950 sm:text-[17px]">
                {alreadyAnswered
                  ? "Verdict submitted"
                  : "Review this pronunciation"}
              </div>
            )}

            <div className="mt-1 flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 text-xs font-bold text-slate-500">
              <span>
                {alreadyAnswered
                  ? "This attempt has already been reviewed."
                  : "Listen first, then rate pronunciation."}
              </span>

              {!alreadyAnswered && (
                <span className="font-black text-blue-700">
                  Open to mark words or create Better Me.
                </span>
              )}
            </div>
          </div>
        </button>

        <div className="grid grid-cols-1 gap-2 sm:flex sm:shrink-0 sm:items-center">
          {hasAudio && canPlay && (
            <button
              type="button"
              onClick={() => playRecording?.({ expandFirst: true })}
              disabled={loadingAudio}
              className={cx(
                "rounded-2xl border bg-white px-4 py-2.5 text-sm font-black shadow-sm transition hover:-translate-y-0.5 disabled:opacity-60",
                alreadyAnswered
                  ? "border-green-300 text-green-800"
                  : "border-blue-300 text-blue-900"
              )}
            >
              {loadingAudio ? "Loading…" : isPlayingAudio ? "■ Stop" : "▶ Play"}
            </button>
          )}

          {hasAudio && !canPlay && (
            <div className="rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-center text-sm font-black text-slate-700 shadow-sm">
              🔒 Log in
            </div>
          )}

          {!hasAudio && (
            <div className="rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-center text-sm font-black text-slate-500 shadow-sm">
              No recording
            </div>
          )}

          {!alreadyAnswered && (
            <button
              type="button"
              onClick={onExpand}
              className="rounded-2xl border border-blue-700 bg-blue-600 px-4 py-2.5 text-sm font-black text-white shadow-lg shadow-blue-600/25 transition hover:-translate-y-0.5 hover:bg-blue-700"
            >
              Review
            </button>
          )}

          {alreadyAnswered && (
            <div className="rounded-2xl border border-green-300 bg-green-100 px-4 py-2.5 text-center text-sm font-black text-green-800 shadow-sm">
              Done
            </div>
          )}
        </div>
      </div>

      {audioError && (
        <div className="mt-3 rounded-2xl border border-red-200 bg-red-50 px-4 py-2 text-sm font-semibold text-red-700">
          {audioError}
        </div>
      )}
    </div>
  );
}

function PlaybackToolbar({
  hasAudio,
  canPlay,

  loadingAudio,
  isPlayingAudio,
  playRecording,

  hasNarratorAudio,
  loadingNarratorAudio,
  isPlayingNarratorAudio,
  playNarratorRecording,

  hasBetterMeFinal,
  hasBetterMePreview,
  betterMeDirty,
  loadingBetterMeAudio,
  isPlayingBetterMeAudio,
  playBetterMeRecording,

  playbackSpeed,
  changePlaybackSpeed,

  hasFinishedPlayback,
  activePitchSource,
}) {
  if (!hasAudio || !canPlay) return null;

  const hasBetterMe = hasBetterMeFinal || hasBetterMePreview;

  const statusText =
    activePitchSource === "narrator"
      ? "Narrator reference"
      : activePitchSource === "better_me"
        ? hasBetterMeFinal
          ? "Saved Better Me"
          : "Better Me preview"
        : "Learner attempt";

  const baseButtonStyle = {
    minHeight: 48,
    borderRadius: 16,
    borderWidth: 2,
    borderStyle: "solid",
    fontWeight: 900,
    fontSize: 14,
    lineHeight: 1,
    boxShadow: "0 6px 16px rgba(15, 23, 42, 0.06)",
    WebkitAppearance: "none",
    appearance: "none",
  };

  const attemptButtonStyle = isPlayingAudio
    ? {
        ...baseButtonStyle,
        backgroundColor: "#2563eb",
        borderColor: "#1d4ed8",
        color: "#ffffff",
        boxShadow: "0 14px 28px rgba(37, 99, 235, 0.28)",
      }
    : activePitchSource === "attempt"
      ? {
          ...baseButtonStyle,
          backgroundColor: "#eff6ff",
          borderColor: "#3b82f6",
          color: "#1e40af",
        }
      : {
          ...baseButtonStyle,
          backgroundColor: "#ffffff",
          borderColor: "#e2e8f0",
          color: "#334155",
        };

  const narratorButtonStyle = isPlayingNarratorAudio
    ? {
        ...baseButtonStyle,
        backgroundColor: "#059669",
        borderColor: "#047857",
        color: "#ffffff",
        boxShadow: "0 14px 28px rgba(5, 150, 105, 0.28)",
      }
    : activePitchSource === "narrator"
      ? {
          ...baseButtonStyle,
          backgroundColor: "#ecfdf5",
          borderColor: "#10b981",
          color: "#065f46",
        }
      : {
          ...baseButtonStyle,
          backgroundColor: "#ffffff",
          borderColor: "#e2e8f0",
          color: "#334155",
        };

  const betterMeButtonStyle = isPlayingBetterMeAudio
    ? {
        ...baseButtonStyle,
        backgroundColor: "#6d28d9",
        borderColor: "#4c1d95",
        color: "#ffffff",
        boxShadow: "0 14px 28px rgba(109, 40, 217, 0.28)",
      }
    : activePitchSource === "better_me"
      ? {
          ...baseButtonStyle,
          backgroundColor: "#f5f3ff",
          borderColor: "#8b5cf6",
          color: "#5b21b6",
          boxShadow: "0 8px 20px rgba(124, 58, 237, 0.12)",
        }
      : {
          ...baseButtonStyle,
          backgroundColor: "#ffffff",
          borderColor: "#ddd6fe",
          color: "#5b21b6",
        };

  return (
    <div className="mt-5 rounded-[24px] border border-blue-100 bg-white/95 p-3 shadow-sm">
      <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <div className="text-[10px] font-black uppercase tracking-[0.16em] text-blue-500">
            Playback
          </div>

          <div className="mt-0.5 text-xs font-semibold text-slate-500">
            {statusText}
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          {hasBetterMePreview && !hasBetterMeFinal && (
            <span
              className={cx(
                "rounded-full border px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.12em]",
                betterMeDirty
                  ? "border-amber-200 bg-amber-50 text-amber-800"
                  : "border-violet-200 bg-violet-50 text-violet-800"
              )}
            >
              {betterMeDirty ? "Preview outdated" : "Preview ready"}
            </span>
          )}

          {hasBetterMeFinal && (
            <span className="rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.12em] text-emerald-800">
              Better Me saved
            </span>
          )}
        </div>
      </div>

      <div
        className={cx(
          "grid grid-cols-1 gap-2 xl:items-center",
          hasBetterMe
            ? "xl:grid-cols-[1fr_1fr_1fr_auto]"
            : hasNarratorAudio
              ? "xl:grid-cols-[1fr_1fr_auto]"
              : "xl:grid-cols-[1fr_auto]"
        )}
      >
        <button
          type="button"
          onClick={playRecording}
          disabled={loadingAudio}
          className="flex min-h-12 items-center justify-center gap-2 rounded-2xl px-4 py-2.5 text-sm font-black transition hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-60"
          style={attemptButtonStyle}
        >
          <span>{loadingAudio ? "…" : isPlayingAudio ? "■" : "▶"}</span>
          <span>
            {loadingAudio
              ? "Loading"
              : isPlayingAudio
                ? "Stop attempt"
                : hasFinishedPlayback
                  ? "Replay attempt"
                  : "Play attempt"}
          </span>
        </button>

        {hasNarratorAudio ? (
          <button
            type="button"
            onClick={playNarratorRecording}
            disabled={loadingNarratorAudio}
            className="flex min-h-12 items-center justify-center gap-2 rounded-2xl px-4 py-2.5 text-sm font-black transition hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-60"
            style={narratorButtonStyle}
          >
            <span>
              {loadingNarratorAudio
                ? "…"
                : isPlayingNarratorAudio
                  ? "■"
                  : "▶"}
            </span>
            <span>
              {loadingNarratorAudio
                ? "Loading"
                : isPlayingNarratorAudio
                  ? "Stop narrator"
                  : "Play narrator"}
            </span>
          </button>
        ) : null}

        {hasBetterMe ? (
          <button
            type="button"
            onClick={playBetterMeRecording}
            disabled={loadingBetterMeAudio}
            className="flex min-h-12 items-center justify-center gap-2 rounded-2xl px-4 py-2.5 text-sm font-black transition hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-60"
            style={betterMeButtonStyle}
            title={
              hasBetterMeFinal
                ? "Play the saved edited learner voice"
                : "Play the temporary Better Me preview"
            }
          >
            <span>
              {loadingBetterMeAudio
                ? "…"
                : isPlayingBetterMeAudio
                  ? "■"
                  : "▶"}
            </span>
            <span>
              {loadingBetterMeAudio
                ? "Loading Better Me"
                : isPlayingBetterMeAudio
                  ? "Stop Better Me"
                  : "Play Better Me"}
            </span>
          </button>
        ) : null}

        <div className="flex min-h-12 items-center justify-between gap-2 rounded-2xl border border-slate-200 bg-white px-3 py-2 shadow-sm xl:w-[210px]">
          <span className="text-[10px] font-black uppercase tracking-[0.14em] text-slate-400">
            Speed
          </span>

          <div className="flex rounded-xl bg-slate-100 p-1">
            {[0.5, 1].map((speed) => (
              <button
                key={speed}
                type="button"
                onClick={() => changePlaybackSpeed(speed)}
                className={cx(
                  "rounded-lg px-3 py-1.5 text-xs font-black transition",
                  playbackSpeed === speed
                    ? "bg-blue-600 text-white shadow-sm"
                    : "text-slate-600 hover:bg-white"
                )}
              >
                {speed === 1 ? "100%" : "50%"}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function getPitchSourceUi({
  activePitchSource,
  betterMeFinal,
  betterMeDirty,
  isPlayingAudio,
  isPlayingNarratorAudio,
  isPlayingBetterMeAudio,
}) {
  if (activePitchSource === "narrator") {
    return {
      label: "Narrator",
      status: isPlayingNarratorAudio ? "Playing" : "Reference",
      title: "Narrator curve",
      subtitle: "Model voice.",
      dotClass: "bg-emerald-500",
      pillClass: "border-emerald-200 bg-emerald-50 text-emerald-800",
      statusClass: "border-emerald-200 bg-white text-emerald-700",
      panelClass: "border-emerald-100 bg-white",
    };
  }

  if (activePitchSource === "better_me") {
    return {
      label: "Better Me",
      status: isPlayingBetterMeAudio
        ? "Playing"
        : betterMeFinal
          ? "Saved"
          : betterMeDirty
            ? "Outdated"
            : "Preview",
      title: betterMeFinal ? "Saved Better Me curve" : "Better Me preview curve",
      subtitle: betterMeFinal ? "Edited voice." : "Temporary edit.",
      dotClass: "bg-violet-500",
      pillClass: "border-violet-200 bg-violet-50 text-violet-800",
      statusClass: betterMeFinal
        ? "border-emerald-200 bg-emerald-50 text-emerald-800"
        : betterMeDirty
          ? "border-amber-200 bg-amber-50 text-amber-800"
          : "border-violet-200 bg-white text-violet-700",
      panelClass: "border-violet-100 bg-white",
    };
  }

  return {
    label: "Learner",
    status: isPlayingAudio ? "Playing" : "Attempt",
    title: "Learner curve",
    subtitle: "Original recording.",
    dotClass: "bg-blue-500",
    pillClass: "border-blue-200 bg-blue-50 text-blue-800",
    statusClass: "border-blue-200 bg-white text-blue-700",
    panelClass: "border-blue-100 bg-white",
  };
}

function LearnerRequestCard({ content }) {
  if (!content) return null;

  return (
    <div className="mt-5 rounded-[22px] border border-blue-200 bg-white px-4 py-4 shadow-sm">
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <span className="text-[10px] font-black uppercase tracking-[0.16em] text-blue-600">
          Learner request
        </span>

        <span className="rounded-full border border-blue-100 bg-blue-50 px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.12em] text-blue-700">
          Review
        </span>
      </div>

      <p className="select-text whitespace-pre-wrap break-words text-[16px] font-black leading-7 text-slate-950">
        “{content}”
      </p>
    </div>
  );
}


function SubmittedComparisonOverlay({ reply, onClose }) {
  const userNBest = getUserNBest(reply?.assessment);
  const humanRatings = reply?.ratings || [];

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/60 px-4 backdrop-blur-sm">
      <div className="max-h-[92vh] w-full max-w-5xl overflow-hidden rounded-3xl bg-white shadow-2xl">
        <div className="flex items-start justify-between border-b bg-gradient-to-r from-blue-50 to-sky-50 p-5 sm:p-6">
          <div>
            <div className="mb-2 text-xs font-bold text-blue-700">
              Verdict submitted
            </div>

            <h2 className="text-2xl font-black text-gray-900">
              AI vs Your Rating
            </h2>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="text-2xl text-gray-500 hover:text-black"
            aria-label="Close comparison"
          >
            ×
          </button>
        </div>

        <div className="max-h-[75vh] overflow-y-auto p-4 sm:p-6">
          <div className="grid gap-6 lg:grid-cols-2">
            <div className="rounded-2xl border border-slate-200 p-4">
              <div className="mb-4 text-center font-bold">🤖 AI Assessment</div>

              <div className="flex flex-wrap justify-center gap-3">
                {userNBest?.Words?.map((word, i) => {
                  if (word.PronunciationAssessment?.ErrorType === "Omission") {
                    return (
                      <div
                        key={i}
                        className="h-3 w-3 rounded-full bg-gray-400"
                      />
                    );
                  }

                  const score =
                    word.PronunciationAssessment?.AccuracyScore || 0;

                  const color =
                    score >= 80 ? "green" : score >= 40 ? "yellow" : "red";

                  return (
                    <div
                      key={i}
                      className="min-w-[80px] rounded-xl border border-slate-200 p-3 text-center"
                    >
                      <div className="mb-2 font-bold">{word.Word}</div>

                      <div className="mb-2 flex justify-center gap-1">
                        {(word.Phonemes || []).map((ph, j) => {
                          const pScore =
                            ph.PronunciationAssessment?.AccuracyScore || score;

                          const pColor =
                            pScore >= 80
                              ? "bg-green-500"
                              : pScore >= 40
                                ? "bg-yellow-500"
                                : "bg-red-500";

                          return (
                            <div
                              key={j}
                              className={cx("h-2 w-2 rounded-full", pColor)}
                              title={String(pScore)}
                            />
                          );
                        })}
                      </div>

                      <div
                        className={cx(
                          "rounded px-2 py-1 text-xs font-bold text-white",
                          color === "green"
                            ? "bg-green-500"
                            : color === "yellow"
                              ? "bg-yellow-500"
                              : "bg-red-500"
                        )}
                      >
                        {score}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 p-4">
              <div className="mb-4 text-center font-bold">🧑 Your Rating</div>

              <div className="flex flex-wrap justify-center gap-3">
                {humanRatings.map((word) => {
                  const meta = colorOf(word.rating);

                  return (
                    <div
                      key={word.index}
                      className={cx(
                        "min-w-[80px] rounded-xl border p-3 text-center",
                        meta.bgClass,
                        meta.borderClass,
                        meta.textClass
                      )}
                    >
                      <div className="mb-2 font-bold">{word.word}</div>

                      <div className="flex flex-wrap justify-center gap-1">
                        {(word.letters || []).map((letter) => {
                          const letterMeta = colorOf(letter.rating);

                          return (
                            <span
                              key={letter.index}
                              className={cx(
                                "rounded px-1 text-xs font-bold",
                                letterMeta.bgClass,
                                letterMeta.textClass
                              )}
                            >
                              {letter.char}
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {reply?.betterMe?.audio_url && (
            <div className="mt-6 rounded-2xl border border-violet-200 bg-violet-50 px-4 py-3 text-sm font-bold text-violet-800">
              Better Me audio was submitted with this verdict.
            </div>
          )}

          <div className="mt-6 text-center">
            <button
              type="button"
              onClick={onClose}
              className="rounded-xl bg-blue-600 px-6 py-2 font-bold text-white hover:bg-blue-700"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function RateMeCommentBlock({
  comment,
  content,
  canInteract,
  payloadBase,
  anchor,
  onSubmitted,
  alreadyAnswered = false,
}) {
  const [loadingAudio, setLoadingAudio] = useState(false);
  const [loadingNarratorAudio, setLoadingNarratorAudio] = useState(false);
  const [loadingBetterMeAudio, setLoadingBetterMeAudio] = useState(false);

  const [audioError, setAudioError] = useState("");
  const [isPlayingAudio, setIsPlayingAudio] = useState(false);
  const [isPlayingNarratorAudio, setIsPlayingNarratorAudio] = useState(false);
  const [isPlayingBetterMeAudio, setIsPlayingBetterMeAudio] = useState(false);

  const [submitError, setSubmitError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submittedReply, setSubmittedReply] = useState(null);
  const [collapsed, setCollapsed] = useState(true);
  const [audioPlayerKey, setAudioPlayerKey] = useState(0);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [hasFinishedPlayback, setHasFinishedPlayback] = useState(false);
  const [pitchExpanded, setPitchExpanded] = useState(true);
  const [activePitchSource, setActivePitchSource] = useState("attempt");
  const [pitchWorkTab, setPitchWorkTab] = useState("view");

  const [betterMePreview, setBetterMePreview] = useState(null);
  const [betterMeDirty, setBetterMeDirty] = useState(false);
  const [betterMeEdit, setBetterMeEdit] = useState(null);
  const [betterMeFinal, setBetterMeFinal] = useState(null);
  const [savingBetterMeFinal, setSavingBetterMeFinal] = useState(false);

  const audioRef = useRef(null);
  const narratorAudioRef = useRef(null);
  const betterMeAudioRef = useRef(null);

  const token = localStorage.getItem("token");
  const audioUrl = comment?.audio?.audio_url;
  const hasAudio = Boolean(audioUrl);
  const canPlay = Boolean(token && canInteract);

  const narratorIntonation =
    comment?.assessment?.narrator_intonation ||
    comment?.assessment?.narratorIntonation ||
    comment?.assessment?.narrator_assessment ||
    null;

  const narratorAudioUrl = getNarratorAudioFetchUrl(narratorIntonation);
  const hasNarratorAudio = Boolean(narratorAudioUrl);

  const effectiveAnchor = useMemo(() => {
    return anchor || comment?.anchor || null;
  }, [anchor, comment?.anchor]);

  const sentenceText = effectiveAnchor?.text || "";
  const chapterDisplay = getChapterDisplay(comment, payloadBase);

  const [sentenceRating, setSentenceRating] = useState("green");
  const [ratings, setRatings] = useState(() => buildRatings(sentenceText));
  const [feedbackText, setFeedbackText] = useState("");

  const markedCount = getMarkedCount(sentenceRating, ratings);

  const attemptPitchForBezier = useMemo(() => {
    return normalizePitchForBezier(comment?.assessment);
  }, [comment?.assessment]);

  const attemptPitchBounds = useMemo(() => {
    return getPitchTimeBounds(comment?.assessment);
  }, [comment?.assessment]);

  const displayedBetterMe = betterMeFinal || betterMePreview;

  const currentAudioRef =
    activePitchSource === "narrator"
      ? narratorAudioRef
      : activePitchSource === "better_me"
        ? betterMeAudioRef
        : audioRef;

  const currentIntonationOverride =
    activePitchSource === "narrator"
      ? narratorIntonation
      : activePitchSource === "better_me"
        ? displayedBetterMe?.intonation || null
        : null;

  const currentPitchTitle =
    activePitchSource === "narrator"
      ? "Narrator melody"
      : activePitchSource === "better_me"
        ? betterMeFinal
          ? "Saved Better Me melody"
          : "Better Me preview melody"
        : "Attempt melody";

  const currentPitchSubtitle =
    activePitchSource === "narrator"
      ? "Reference contour from the narrator."
      : activePitchSource === "better_me"
        ? betterMeFinal
          ? "Final edited learner voice attached to this verdict."
          : "Temporary edited learner voice. Save it to accept it."
        : hasFinishedPlayback
          ? "Attempt reviewed. Open again to inspect the voice melody."
          : "Preview the learner attempt, then press play to begin rating.";

  const pitchSourceUi = getPitchSourceUi({
    activePitchSource,
    betterMeFinal,
    betterMeDirty,
    isPlayingAudio,
    isPlayingNarratorAudio,
    isPlayingBetterMeAudio,
  });

  useEffect(() => {
    setSentenceRating("green");
    setRatings(buildRatings(sentenceText));
    setFeedbackText("");
    setSubmitError("");
    setCollapsed(true);

    setIsPlayingAudio(false);
    setIsPlayingNarratorAudio(false);
    setIsPlayingBetterMeAudio(false);

    setLoadingAudio(false);
    setLoadingNarratorAudio(false);
    setLoadingBetterMeAudio(false);

    setPlaybackSpeed(1);
    setHasFinishedPlayback(false);
    setPitchExpanded(true);
    setActivePitchSource("attempt");
    setPitchWorkTab("view");

    setBetterMePreview(null);
    setBetterMeDirty(false);
    setBetterMeEdit(null);
    setBetterMeFinal(null);
    setSavingBetterMeFinal(false);

    setAudioPlayerKey((value) => value + 1);
  }, [sentenceText, comment?.id]);

  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();

        if (audioRef.current.src?.startsWith("blob:")) {
          URL.revokeObjectURL(audioRef.current.src);
        }

        audioRef.current = null;
      }

      if (narratorAudioRef.current) {
        narratorAudioRef.current.pause();

        if (narratorAudioRef.current.src?.startsWith("blob:")) {
          URL.revokeObjectURL(narratorAudioRef.current.src);
        }

        narratorAudioRef.current = null;
      }

      if (betterMeAudioRef.current) {
        betterMeAudioRef.current.pause();

        if (betterMeAudioRef.current.src?.startsWith("blob:")) {
          URL.revokeObjectURL(betterMeAudioRef.current.src);
        }

        betterMeAudioRef.current = null;
      }

      setIsPlayingAudio(false);
      setIsPlayingNarratorAudio(false);
      setIsPlayingBetterMeAudio(false);
      setLoadingBetterMeAudio(false);
    };
  }, []);

  const waitForPaint = async () => {
    await new Promise((resolve) => requestAnimationFrame(resolve));
    await new Promise((resolve) => requestAnimationFrame(resolve));
  };

  const changePlaybackSpeed = (nextSpeed) => {
    setPlaybackSpeed(nextSpeed);

    if (audioRef.current) {
      audioRef.current.playbackRate = nextSpeed;
    }

    if (narratorAudioRef.current) {
      narratorAudioRef.current.playbackRate = nextSpeed;
    }

    if (betterMeAudioRef.current) {
      betterMeAudioRef.current.playbackRate = nextSpeed;
    }

    setAudioPlayerKey((value) => value + 1);
  };

  const stopUserRecording = () => {
    if (!audioRef.current) {
      setIsPlayingAudio(false);
      return;
    }

    audioRef.current.pause();
    audioRef.current.currentTime = 0;

    if (audioRef.current.src?.startsWith("blob:")) {
      URL.revokeObjectURL(audioRef.current.src);
    }

    audioRef.current = null;
    setIsPlayingAudio(false);
    setAudioPlayerKey((value) => value + 1);
  };

  const stopNarratorRecording = () => {
    if (!narratorAudioRef.current) {
      setIsPlayingNarratorAudio(false);
      return;
    }

    narratorAudioRef.current.pause();
    narratorAudioRef.current.currentTime = 0;

    if (narratorAudioRef.current.src?.startsWith("blob:")) {
      URL.revokeObjectURL(narratorAudioRef.current.src);
    }

    narratorAudioRef.current = null;
    setIsPlayingNarratorAudio(false);
    setAudioPlayerKey((value) => value + 1);
  };

  const stopBetterMeRecording = () => {
    if (!betterMeAudioRef.current) {
      setIsPlayingBetterMeAudio(false);
      return;
    }

    betterMeAudioRef.current.pause();
    betterMeAudioRef.current.currentTime = 0;

    if (betterMeAudioRef.current.src?.startsWith("blob:")) {
      URL.revokeObjectURL(betterMeAudioRef.current.src);
    }

    betterMeAudioRef.current = null;
    setIsPlayingBetterMeAudio(false);
    setAudioPlayerKey((value) => value + 1);
  };

  const stopAllAudio = () => {
    stopUserRecording();
    stopNarratorRecording();
    stopBetterMeRecording();
  };

  const playRecording = async ({ expandFirst = false } = {}) => {
    if (expandFirst) {
      setCollapsed(false);
      setActivePitchSource("attempt");
      setPitchWorkTab("view");
      setPitchExpanded(true);
      await waitForPaint();
    }

    if (isPlayingAudio) {
      stopUserRecording();
      return;
    }

    const fetchUrl = getAudioFetchUrl(audioUrl);
    if (!fetchUrl || !canPlay) return;

    setLoadingAudio(true);
    setAudioError("");

    try {
      stopAllAudio();

      setActivePitchSource("attempt");
      setPitchWorkTab("view");
      setPitchExpanded(true);

      const res = await fetch(fetchUrl, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.status === 401 || res.status === 403) {
        throw new Error("LOGIN_REQUIRED");
      }

      if (!res.ok) throw new Error(`Audio ${res.status}`);

      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      const player = new Audio(objectUrl);

      player.playbackRate = playbackSpeed;

      audioRef.current = player;
      setAudioPlayerKey((value) => value + 1);

      player.onplay = () => {
        setIsPlayingAudio(true);
        setIsPlayingNarratorAudio(false);
        setIsPlayingBetterMeAudio(false);

        setActivePitchSource("attempt");
        setPitchWorkTab("view");
        setPitchExpanded(true);
      };

      player.onpause = () => {
        setIsPlayingAudio(false);
      };

      player.onended = () => {
        URL.revokeObjectURL(objectUrl);
        audioRef.current = null;
        setIsPlayingAudio(false);
        setPitchExpanded(false);
        setHasFinishedPlayback(true);
        setAudioPlayerKey((value) => value + 1);
      };

      player.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        audioRef.current = null;
        setIsPlayingAudio(false);
        setAudioError("Could not play attempt audio.");
        setAudioPlayerKey((value) => value + 1);
      };

      await player.play();
    } catch (error) {
      console.error("play rate me audio failed:", error);

      setIsPlayingAudio(false);

      setAudioError(
        error.message === "LOGIN_REQUIRED"
          ? "Log in to listen and rate."
          : "Could not play attempt audio."
      );
    } finally {
      setLoadingAudio(false);
    }
  };

  const playNarratorRecording = async () => {
    if (isPlayingNarratorAudio) {
      stopNarratorRecording();
      return;
    }

    if (!narratorAudioUrl || !hasNarratorAudio) return;

    setCollapsed(false);
    setActivePitchSource("narrator");
    setPitchWorkTab("view");
    setPitchExpanded(true);

    setLoadingNarratorAudio(true);
    setAudioError("");

    try {
      stopAllAudio();

      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await fetch(narratorAudioUrl, { headers });

      if (res.status === 401 || res.status === 403) {
        throw new Error("LOGIN_REQUIRED");
      }

      if (!res.ok) throw new Error(`Narrator audio ${res.status}`);

      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      const player = new Audio(objectUrl);

      player.playbackRate = playbackSpeed;

      narratorAudioRef.current = player;
      setAudioPlayerKey((value) => value + 1);

      player.onplay = () => {
        setIsPlayingNarratorAudio(true);
        setIsPlayingAudio(false);
        setIsPlayingBetterMeAudio(false);

        setActivePitchSource("narrator");
        setPitchWorkTab("view");
        setPitchExpanded(true);
      };

      player.onpause = () => {
        setIsPlayingNarratorAudio(false);
      };

      player.onended = () => {
        URL.revokeObjectURL(objectUrl);
        narratorAudioRef.current = null;
        setIsPlayingNarratorAudio(false);
        setAudioPlayerKey((value) => value + 1);
      };

      player.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        narratorAudioRef.current = null;
        setIsPlayingNarratorAudio(false);
        setAudioError("Could not play narrator audio.");
        setAudioPlayerKey((value) => value + 1);
      };

      await player.play();
    } catch (error) {
      console.error("play narrator audio failed:", error);

      setIsPlayingNarratorAudio(false);

      setAudioError(
        error.message === "LOGIN_REQUIRED"
          ? "Log in to listen."
          : "Could not play narrator audio."
      );
    } finally {
      setLoadingNarratorAudio(false);
    }
  };

  const playBetterMeRecording = async () => {
    const betterMeAudioUrl =
      betterMeFinal?.audio_url ||
      betterMePreview?.audio_url ||
      comment?.betterMe?.audio_url ||
      comment?.better_me?.audio_url ||
      null;

    if (!betterMeAudioUrl) {
      setAudioError("No Better Me audio was found.");
      return;
    }

    if (isPlayingBetterMeAudio) {
      stopBetterMeRecording();
      return;
    }

    const fetchUrl = getAudioFetchUrl(betterMeAudioUrl);
    if (!fetchUrl || !canPlay) return;

    setCollapsed(false);
    setActivePitchSource("better_me");
    setPitchWorkTab("view");
    setPitchExpanded(true);

    setLoadingBetterMeAudio(true);
    setAudioError("");

    try {
      stopAllAudio();

      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await fetch(fetchUrl, { headers });

      if (res.status === 401 || res.status === 403) {
        throw new Error("LOGIN_REQUIRED");
      }

      if (!res.ok) {
        throw new Error(`Better Me audio ${res.status}`);
      }

      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      const player = new Audio(objectUrl);

      player.playbackRate = playbackSpeed;

      betterMeAudioRef.current = player;
      setAudioPlayerKey((value) => value + 1);

      player.onplay = () => {
        setIsPlayingBetterMeAudio(true);
        setIsPlayingAudio(false);
        setIsPlayingNarratorAudio(false);

        setActivePitchSource("better_me");
        setPitchWorkTab("view");
        setPitchExpanded(true);
      };

      player.onpause = () => {
        setIsPlayingBetterMeAudio(false);
      };

      player.onended = () => {
        URL.revokeObjectURL(objectUrl);
        betterMeAudioRef.current = null;
        setIsPlayingBetterMeAudio(false);
        setAudioPlayerKey((value) => value + 1);
      };

      player.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        betterMeAudioRef.current = null;
        setIsPlayingBetterMeAudio(false);
        setAudioError("Could not play Better Me audio.");
        setAudioPlayerKey((value) => value + 1);
      };

      await player.play();
    } catch (error) {
      console.error("play Better Me audio failed:", error);

      setIsPlayingBetterMeAudio(false);

      setAudioError(
        error.message === "LOGIN_REQUIRED"
          ? "Log in to listen to Better Me."
          : "Could not play Better Me audio."
      );
    } finally {
      setLoadingBetterMeAudio(false);
    }
  };

  const setWordRating = (wordIndex, rating) => {
    setRatings((prev) =>
      prev.map((word) =>
        word.index === wordIndex ? { ...word, rating } : word
      )
    );
  };

  const cycleLetterRating = (wordIndex, letterIndex) => {
    setRatings((prev) =>
      prev.map((word) => {
        if (word.index !== wordIndex) return word;

        return {
          ...word,
          letters: word.letters.map((letter) => {
            if (letter.index !== letterIndex || !letter.rateable) return letter;

            const currentIndex = COLORS.indexOf(letter.rating || "green");
            const nextRating = COLORS[(currentIndex + 1) % COLORS.length];

            return { ...letter, rating: nextRating };
          }),
        };
      })
    );
  };

  const handleBetterMePreviewChange = ({ preview, dirty, edit }) => {
    setBetterMePreview(preview || null);
    setBetterMeDirty(Boolean(dirty));
    setBetterMeEdit(edit || null);
    setSubmitError("");

    setPitchWorkTab("edit");

    if (preview?.audio_url && !dirty) {
      setAudioPlayerKey((value) => value + 1);
    }
  };

  const handleSaveBetterMe = async (accepted) => {
    if (!accepted?.audio_id || !accepted?.audio_url) {
      setAudioError("Create Better Me first.");
      return;
    }

    setSavingBetterMeFinal(true);
    setAudioError("");
    setSubmitError("");

    try {
      stopAllAudio();

      let finalBetterMe = {
        ...accepted,
        kind: "better_me_final",
        transform: accepted.transform || betterMeEdit,
      };

      try {
        const res = await fetch(
          `${process.env.REACT_APP_USER_API_URL}/audio/private/${accepted.audio_id}/better-me-submit`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            body: JSON.stringify({
              parentCommentId: comment?.id,
              transform: accepted.transform || betterMeEdit,
            }),
          }
        );

        if (res.ok) {
          const data = await res.json().catch(() => ({}));

          finalBetterMe = {
            ...finalBetterMe,
            ...data,
            audio_id: data.audio_id || finalBetterMe.audio_id,
            audio_url: data.audio_url || finalBetterMe.audio_url,
            source_audio_id:
              data.source_audio_id || finalBetterMe.source_audio_id,
            kind: data.kind || "better_me_final",
            intonation: data.intonation || finalBetterMe.intonation,
          };
        } else {
          console.warn("better-me-submit route returned", res.status);
        }
      } catch (routeError) {
        console.warn("better-me-submit route is not available yet:", routeError);
      }

      setBetterMeFinal(finalBetterMe);
      setBetterMePreview(finalBetterMe);
      setBetterMeDirty(false);
      setBetterMeEdit(finalBetterMe.transform || betterMeEdit);

      setActivePitchSource("better_me");
      setPitchWorkTab("view");
      setPitchExpanded(true);
      setAudioPlayerKey((value) => value + 1);
    } catch (error) {
      console.error("save Better Me failed:", error);
      setAudioError("Could not save Better Me.");
    } finally {
      setSavingBetterMeFinal(false);
    }
  };

  const submitRating = async () => {
  if (!canInteract) {
    setSubmitError("Log in to submit your rating.");
    return;
  }

  if (hasAudio && !hasFinishedPlayback) {
    setSubmitError(
      "Please listen to the full attempt before submitting your verdict."
    );
    return;
  }

  if (betterMePreview && !betterMeFinal) {
    setSubmitError(
      "You created a Better Me preview, but it is not saved yet. Click Save Better Me to include it, or reset the editor if you do not want to attach it."
    );
    return;
  }

  if (!payloadBase || !effectiveAnchor?.text || !comment?.id) {
    setSubmitError("Could not submit because this request is missing data.");
    return;
  }

  setSubmitting(true);
  setSubmitError("");

  try {
    const betterMePayload = betterMeFinal
      ? {
          audio_id: betterMeFinal.audio_id,
          audio_url: betterMeFinal.audio_url,
          source_audio_id: betterMeFinal.source_audio_id,
          kind: betterMeFinal.kind || "better_me_final",
          edit: betterMeFinal.transform || betterMeEdit || null,
          transform: betterMeFinal.transform || betterMeEdit || null,
          intonation: betterMeFinal.intonation || null,
        }
      : null;

    const ratingsPayload = ratings.map((word) => ({
      word: word.word,
      index: word.index,
      rating: word.rating,
      letters: word.letters
        .filter((letter) => letter.rateable)
        .map((letter) => ({
          char: letter.char,
          index: letter.index,
          rating: letter.rating || "green",
        })),
    }));

    const submitPayload = {
      ...payloadBase,
      chapterTitle: chapterDisplay,
      anchor: effectiveAnchor,
      parentCommentId: comment.id,
      content: feedbackText.trim() || "Human pronunciation verdict",
      sentenceRating,
      ratings: ratingsPayload,

      // Canonical field.
      betterMe: betterMePayload,

      // Compatibility field while backend/frontend naming settles.
      better_me: betterMePayload,

      // Easy backend lookup.
      betterMeTempAudioId: betterMeFinal?.audio_id || null,

      // Optional compact audio mirror for older reply rendering logic.
      betterMeAudio: betterMePayload
        ? {
            kind: "better_me_final",
            audio_id: betterMePayload.audio_id,
            audio_url: betterMePayload.audio_url,
            source_audio_id: betterMePayload.source_audio_id,
            is_private: true,
          }
        : null,
    };

    console.groupCollapsed("[RateMe] Submit verdict");
    console.log("comment.id:", comment?.id);
    console.log("hasFinishedPlayback:", hasFinishedPlayback);
    console.log("betterMePreview:", betterMePreview);
    console.log("betterMeFinal:", betterMeFinal);
    console.log("betterMeEdit:", betterMeEdit);
    console.log("betterMePayload:", betterMePayload);
    console.log("submitPayload:", submitPayload);
    console.groupEnd();

    if (betterMePreview && !betterMePayload) {
      console.warn(
        "[RateMe] Better Me preview exists, but betterMePayload is null. This means betterMeFinal was not set when Submit verdict was clicked.",
        {
          betterMePreview,
          betterMeFinal,
          betterMeEdit,
        }
      );
    }

    const res = await fetch(
      `${process.env.REACT_APP_USER_API_URL}/comments/rate-me-reply`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(submitPayload),
      }
    );

    console.log("[RateMe] Submit response status:", res.status, res.statusText);

    if (!res.ok) {
      let errorText = "";

      try {
        errorText = await res.text();
      } catch {
        errorText = "";
      }

      console.error("[RateMe] Submit failed response body:", errorText);

      throw new Error(`Submit ${res.status}`);
    }

    const created = await res.json();

    console.groupCollapsed("[RateMe] Created reply from backend");
    console.log("created:", created);
    console.log("created.betterMe:", created?.betterMe);
    console.log("created.better_me:", created?.better_me);
    console.log("created.betterMeAudio:", created?.betterMeAudio);
    console.log("created.audio:", created?.audio);
    console.groupEnd();

    if (betterMePayload && !created?.betterMe && !created?.better_me) {
      console.warn(
        "[RateMe] Better Me was sent, but backend response does not contain betterMe/better_me. Backend probably drops the field in /comments/rate-me-reply.",
        {
          sentBetterMe: betterMePayload,
          created,
        }
      );
    }

    setSubmittedReply(created);
    setCollapsed(true);

    setSentenceRating("green");
    setRatings(buildRatings(sentenceText));
    setFeedbackText("");
    setBetterMePreview(null);
    setBetterMeDirty(false);
    setBetterMeEdit(null);
    setBetterMeFinal(null);
  } catch (error) {
    console.error("submit rate me reply failed:", error);
    setSubmitError("Could not submit your rating. Please try again.");
  } finally {
    setSubmitting(false);
  }
};

  const submitButtonStyle = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: 190,
    width: "auto",
    padding: "12px 24px",
    borderRadius: 16,
    backgroundColor: submitting ? "#334155" : "#0f172a",
    color: "#ffffff",
    border: "2px solid #020617",
    boxShadow: "0 12px 28px rgba(15, 23, 42, 0.35)",
    fontSize: 14,
    fontWeight: 900,
    lineHeight: 1,
    textAlign: "center",
    whiteSpace: "nowrap",
    cursor: submitting ? "wait" : "pointer",
    opacity: 1,
    visibility: "visible",
    WebkitAppearance: "none",
    appearance: "none",
  };

  if (collapsed) {
    return (
      <>
        <RateMeCollapsedPreview
          sentence={content || sentenceText}
          hasAudio={hasAudio}
          canPlay={canPlay}
          loadingAudio={loadingAudio}
          isPlayingAudio={isPlayingAudio}
          playRecording={playRecording}
          audioError={audioError}
          onExpand={() => setCollapsed(false)}
          alreadyAnswered={alreadyAnswered}
        />

        {submittedReply && (
          <SubmittedComparisonOverlay
            reply={submittedReply}
            onClose={async () => {
              setSubmittedReply(null);
              await onSubmitted?.();
            }}
          />
        )}
      </>
    );
  }

  return (
    <div className="mt-5 min-w-0 max-w-full overflow-hidden rounded-[28px] border border-blue-300 bg-gradient-to-br from-white via-blue-50 to-sky-50 p-3 shadow-sm sm:p-5">
      <div className="flex min-w-0 items-start justify-between gap-4">
        <div className="flex min-w-0 items-start gap-3 sm:gap-4">
          <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl border border-blue-100 bg-white text-2xl shadow-sm">
            🎙️
          </div>

          <div className="min-w-0">
            <h3 className="text-xl font-black text-slate-950">
              Review this pronunciation
            </h3>

            <p className="mt-1 max-w-xl text-sm font-semibold leading-5 text-slate-600">
              Listen, rate, or create a Better Me version from the learner’s own
              voice.
            </p>
          </div>
        </div>

        <button
          type="button"
          onClick={() => setCollapsed(true)}
          className="shrink-0 rounded-2xl border border-slate-200 bg-white/80 px-4 py-2 text-xs font-black text-slate-500 shadow-sm transition hover:bg-white hover:text-slate-900"
        >
          Collapse
        </button>
      </div>

      <LearnerRequestCard content={content} />

     <PlaybackToolbar
  hasAudio={hasAudio}
  canPlay={canPlay}
  loadingAudio={loadingAudio}
  isPlayingAudio={isPlayingAudio}
  playRecording={() => playRecording()}
  hasNarratorAudio={hasNarratorAudio}
  loadingNarratorAudio={loadingNarratorAudio}
  isPlayingNarratorAudio={isPlayingNarratorAudio}
  playNarratorRecording={playNarratorRecording}
  hasBetterMeFinal={Boolean(betterMeFinal?.audio_url)}
  hasBetterMePreview={Boolean(betterMePreview?.audio_url)}
  betterMeDirty={betterMeDirty}
  loadingBetterMeAudio={loadingBetterMeAudio}
  isPlayingBetterMeAudio={isPlayingBetterMeAudio}
  playBetterMeRecording={playBetterMeRecording}
  playbackSpeed={playbackSpeed}
  changePlaybackSpeed={changePlaybackSpeed}
  hasFinishedPlayback={hasFinishedPlayback}
  activePitchSource={activePitchSource}
/>

      {hasAudio && !canPlay && (
        <div className="mt-5 rounded-[22px] border border-blue-200 bg-white px-5 py-4 text-center shadow-sm">
          <div className="flex items-center justify-center gap-2 text-sm font-black text-slate-950">
            <span>🔒</span>
            <span>Log in to listen</span>
          </div>
        </div>
      )}

      {!hasAudio && (
        <div className="mt-5 rounded-[22px] border border-slate-200 bg-white px-5 py-4 text-center text-sm font-black text-slate-500 shadow-sm">
          No recording
        </div>
      )}

      <div
        className={cx(
          "mt-5 rounded-[24px] border p-3 shadow-sm sm:p-4",
          pitchSourceUi.panelClass
        )}
      >
        <style>{`
          .rate-me-pitch-panel-clean [aria-label="Hide graph"],
          .rate-me-pitch-panel-clean [aria-label="Show graph"] {
            display: none !important;
          }
        `}</style>

        <div className="mb-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex min-w-0 flex-wrap items-center gap-2">
            <span className="text-[10px] font-black uppercase tracking-[0.16em] text-slate-400">
              Pitch work
            </span>

            <span
              className={cx(
                "inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-black",
                pitchSourceUi.pillClass
              )}
            >
              <span className={cx("h-2 w-2 rounded-full", pitchSourceUi.dotClass)} />
              {pitchSourceUi.label}
            </span>

            <span
              className={cx(
                "rounded-full border px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.12em]",
                pitchSourceUi.statusClass
              )}
            >
              {pitchSourceUi.status}
            </span>
          </div>

          <div className="flex rounded-2xl border border-slate-200 bg-slate-50 p-1 shadow-sm">
            <button
              type="button"
              onClick={() => {
                setPitchWorkTab("view");
                setPitchExpanded(true);
              }}
              className={cx(
                "rounded-xl px-3 py-2 text-xs font-black transition",
                pitchWorkTab === "view"
                  ? "bg-white text-blue-700 shadow-sm"
                  : "text-slate-500 hover:text-slate-900"
              )}
            >
              Curve
            </button>

            <button
              type="button"
              onClick={() => {
                setPitchWorkTab("edit");
                setPitchExpanded(true);
              }}
              className={cx(
                "rounded-xl px-3 py-2 text-xs font-black transition",
                pitchWorkTab === "edit"
                  ? "bg-white text-violet-700 shadow-sm"
                  : "text-slate-500 hover:text-slate-900"
              )}
            >
              Edit Better Me
            </button>
          </div>
        </div>

        {pitchWorkTab === "view" && (
          <div className="rate-me-pitch-panel-clean">
            <PitchCurveAudioPanel
              key={`${audioPlayerKey}-${activePitchSource}`}
              assessment={comment?.assessment}
              intonationOverride={currentIntonationOverride}
              audioRef={currentAudioRef}
              speed={playbackSpeed}
              expanded={true}
              onExpandedChange={() => {}}
              title={pitchSourceUi.title}
              subtitle={pitchSourceUi.subtitle}
              compact
            />
          </div>
        )}

        {pitchWorkTab === "edit" && (
          <CurveEditingBlock
            apiBaseUrl={process.env.REACT_APP_USER_API_URL}
            token={token}
            canInteract={canInteract}
            canPlay={canPlay}
            sourceAudioUrl={audioUrl}
            parentCommentId={comment?.id}
            meta={{
              language: payloadBase?.subject?.language,
              type: payloadBase?.subject?.type,
              author: payloadBase?.subject?.author,
              title: payloadBase?.subject?.title,
              chapterTitle: chapterDisplay,
              anchor: effectiveAnchor,
            }}
            originalPitch={attemptPitchForBezier}
            start={attemptPitchBounds.start}
            end={attemptPitchBounds.end}
            playbackSpeed={playbackSpeed}
            audioRef={betterMeAudioRef}
            stopOtherAudio={stopAllAudio}
            onError={setAudioError}
            onPreviewChange={handleBetterMePreviewChange}
            onSaveBetterMe={handleSaveBetterMe}
          />
        )}
      </div>

      {canInteract ? (
        <>
          <SmoothReveal show={!hasFinishedPlayback}>
            <div className="mt-5 rounded-[24px] border border-blue-200 bg-white/90 px-4 py-4 text-sm font-bold text-blue-800 shadow-sm">
              Step 1 of 2 · Listen to the full attempt to unlock rating.
            </div>
          </SmoothReveal>

          <SmoothReveal show={hasFinishedPlayback}>
            <div
              className={cx(
                "mt-5 min-w-0 rounded-[24px] shadow-sm transition-colors",
                tintClasses(sentenceRating)
              )}
            >
              <div className="p-3 sm:p-4">
                <div className="mb-4 rounded-2xl border border-slate-200 bg-white/80 px-4 py-3 text-sm font-black text-slate-700">
                  Step 2 of 2 · Mark the sentence, words, or letters.
                </div>

                <div className="mt-4 rounded-3xl border border-slate-200 bg-white/80 p-3 shadow-sm sm:p-4">
                  <div className="flex min-w-0 flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                    <div className="min-w-0">
                      <div className="text-sm font-black text-slate-950">
                        Whole sentence verdict
                      </div>

                      <div className="mt-0.5 text-xs font-semibold text-slate-500">
                        Accuracy, rhythm, intonation, and flow
                      </div>
                    </div>

                    <CircleRadioGroup
                      value={sentenceRating}
                      onChange={setSentenceRating}
                    />
                  </div>
                </div>

                <div className="mt-4 min-w-0 overflow-x-auto rounded-3xl border border-slate-200 bg-slate-50/70 p-3 sm:p-4">
                  <div className="flex min-w-0 flex-wrap items-end gap-3 sm:gap-x-7 sm:gap-y-6">
                    {ratings.map((word) => (
                      <div
                        key={`${word.word}-${word.index}`}
                        className={cx(
                          "flex min-w-0 max-w-full flex-col items-center gap-2 rounded-2xl px-2 py-3 shadow-sm transition-colors sm:px-3",
                          tintClasses(word.rating)
                        )}
                      >
                        <div className="flex max-w-full flex-wrap items-center justify-center gap-1">
                          {word.letters.map((letter) => (
                            <LetterButton
                              key={`${word.index}-${letter.index}`}
                              letter={letter}
                              onClick={() =>
                                cycleLetterRating(word.index, letter.index)
                              }
                            />
                          ))}
                        </div>

                        <CircleRadioGroup
                          compact
                          value={word.rating}
                          onChange={(next) => setWordRating(word.index, next)}
                        />
                      </div>
                    ))}
                  </div>
                </div>

                <textarea
                  value={feedbackText}
                  onChange={(e) => setFeedbackText(e.target.value)}
                  className="mt-4 min-h-[82px] w-full resize-y rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-900 outline-none focus:border-blue-300 focus:ring-4 focus:ring-blue-100"
                  placeholder="Optional note..."
                />

                {betterMeFinal?.audio_url && (
                  <div className="mt-4 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-bold text-emerald-800">
                    Saved Better Me is attached. You can play it from the
                    playback bar or inspect its curve here.
                  </div>
                )}

                {betterMePreview?.audio_url && !betterMeFinal && (
                  <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm font-bold text-amber-800">
                    Better Me exists as a preview only. Save Better Me before
                    submitting if you want to include it.
                  </div>
                )}

                {submitError && (
                  <div className="mt-3 rounded-2xl border border-red-200 bg-red-50 px-4 py-2 text-sm font-semibold text-red-700">
                    {submitError}
                  </div>
                )}
              </div>

              <div
                className="sticky bottom-0 z-30 px-3 py-3 sm:px-4"
                style={{
                  backgroundColor: "#f8fafc",
                  borderTop: "1px solid #cbd5e1",
                  boxShadow: "0 -12px 28px rgba(15, 23, 42, 0.16)",
                }}
              >
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div className="text-xs font-bold text-slate-700">
                    {markedCount === 0
                      ? "No issues marked. This will submit as all good."
                      : `${markedCount} item${
                          markedCount === 1 ? "" : "s"
                        } marked for attention.`}
                  </div>

                  <button
                    type="button"
                    onClick={submitRating}
                    disabled={submitting}
                    aria-busy={submitting}
                    style={submitButtonStyle}
                  >
                    {submitting ? "Submitting…" : "Submit verdict"}
                  </button>
                </div>
              </div>
            </div>
          </SmoothReveal>
        </>
      ) : (
        <div className="mt-5 rounded-2xl border border-slate-200 bg-white/90 px-4 py-3 text-sm font-bold text-slate-700">
          🔒 Log in to leave your human verdict.
        </div>
      )}

      {audioError && (
        <div className="mt-4 rounded-2xl border border-red-200 bg-red-50 px-4 py-2 text-sm font-semibold text-red-700">
          {audioError}
        </div>
      )}

      {savingBetterMeFinal && (
        <div className="mt-4 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm font-bold text-emerald-800">
          Saving Better Me…
        </div>
      )}

      {submittedReply && (
        <SubmittedComparisonOverlay
          reply={submittedReply}
          onClose={async () => {
            setSubmittedReply(null);
            await onSubmitted?.();
          }}
        />
      )}
    </div>
  );
}